import { PlayerInfoWithProjectionsFragment } from '@/api/events/query.generated';

/**
 * TEMPORARY tennis player matching between the picks BE and PAM.
 *
 * Pickem and PAM are separate feeds with no shared player id, so we reconcile them by name:
 * - `getTennisMatchup` parses the players out of the pickem event name (the official draw order).
 * - `orderTennisMatchupByPamSides` reorders that pair to PAM's away/home sides so side-keyed data
 *   (scores, live photos) pairs with the right player.
 *
 * This name-based matching is fragile and should be removed once a proper id-based matching
 * mechanism exists between PAM and picks.
 */

export type TennisMatchPlayer = {
    firstName?: string;
    lastName?: string;
    icon?: string;
    /** The matched player from the BE, if any. Undefined when we fell back to the event name. */
    player?: Partial<PlayerInfoWithProjectionsFragment>;
};

// Strips diacritics and non-alphanumerics so "Muñar" / "Munar" and
// "Auger-Aliassime" / "Auger Aliassime" compare equal.
const normalizeForMatch = (s: string | undefined | null) =>
    s
        ?.normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/gi, '')
        .toLowerCase() ?? '';

/**
 * Builds the tennis matchup from the event name, which is the source of truth
 * for player order. Event name format: "lastName, firstName vs lastName, firstName".
 * BE may return players in a different order or omit one (e.g. missing projections),
 * so we match each parsed name to a BE player by last name when possible and
 * fall back to the parsed name only otherwise.
 */
export const getTennisMatchup = (
    players: ReadonlyArray<Partial<PlayerInfoWithProjectionsFragment> | undefined | null>,
    eventName: string | undefined
): [TennisMatchPlayer, TennisMatchPlayer] => {
    const parsed = (eventName?.split(' vs ') ?? []).map(part => {
        const [lastName, firstName] = part.split(', ').map(s => s.trim());
        return { lastName, firstName };
    });

    const build = (parsedName: { firstName?: string; lastName?: string } | undefined): TennisMatchPlayer => {
        if (!parsedName?.lastName) {
            return {};
        }
        const target = normalizeForMatch(parsedName.lastName);
        const match = target ? players?.find(p => normalizeForMatch(p?.lastName) === target) : undefined;
        return match
            ? { firstName: match.firstName, lastName: match.lastName, icon: match.icon, player: match }
            : parsedName;
    };

    return [build(parsed[0]), build(parsed[1])];
};

// for tennis, the PAM team name fields hold the player's name (e.g. short_code "C. Gauff")
type PamTeamName = { short_code?: string; name?: string };

const pamTeamMatchesLastName = (team: PamTeamName | undefined, lastName: string | undefined) => {
    const normalizedLastName = normalizeForMatch(lastName);
    if (!normalizedLastName) {
        return false;
    }
    // the PAM name (e.g. "C. Gauff") ends with the player's last name, so check both name fields
    const teamNames = [team?.short_code, team?.name];
    return teamNames.some(teamName => {
        const normalizedTeamName = normalizeForMatch(teamName);
        return !!normalizedTeamName && normalizedTeamName.endsWith(normalizedLastName);
    });
};

/**
 * Orders the players to PAM's [away, home] sides by matching their last names against the PAM
 * team names, so side data like scoring pairs with the right player. The pickem
 * event name follows the official tournament draw, which may not match PAM's away/home.
 * Keeps the given order when there is no PAM event or the names can't be matched.
 */
export const orderTennisMatchupByPamSides = (
    matchup: [TennisMatchPlayer, TennisMatchPlayer],
    pamEvent?: { away_team: PamTeamName; home_team: PamTeamName }
): [TennisMatchPlayer, TennisMatchPlayer] => {
    if (!pamEvent) {
        return matchup;
    }
    const sideOf = (player: TennisMatchPlayer): 'away' | 'home' | undefined => {
        const away = pamTeamMatchesLastName(pamEvent.away_team, player.lastName);
        const home = pamTeamMatchesLastName(pamEvent.home_team, player.lastName);
        if (away === home) {
            // no match, or both sides match
            return undefined;
        }
        return away ? 'away' : 'home';
    };

    const [player1, player2] = matchup;
    const player1Side = sideOf(player1);
    const player2Side = sideOf(player2);
    if (player1Side && player1Side === player2Side) {
        // both players resolved to the same side - conflicting data, keep the given order
        return matchup;
    }
    return player1Side === 'home' || player2Side === 'away' ? [player2, player1] : matchup;
};
